import { z } from 'zod';
import { slugSchema } from './slug';

// Conversation A/B/C experiments. "experiment" is the config entity; "variant"
// (arm) is one A/B/C bucket; an `agent_variant_override` is a sparse per-agent
// behavioral overlay on one arm. experiment_slug = optional immutable wire
// identifier (CLI hub-as-code filename). experiment_name = freely editable
// display label.

// ai_mode mirrors the hub-level enum (HubDO schema); NULL on a variant = inherit
// the hub's ai_mode at turn time (PR2).
const variantAiModeSchema = z.enum(['pilot+copilot', 'pilot', 'copilot', 'turned_off']);
const experimentStatusSchema = z.enum(['draft', 'running', 'stopped']);

// {} and null collapse to null at the contract boundary; undefined stays
// undefined so PATCH partial-update guards still work (mirrors states.ts).
const overlayUpdateSchema = z
  .record(z.unknown())
  .nullish()
  .transform((v) => (v === undefined ? undefined : v && Object.keys(v).length > 0 ? v : null));

// Param schemas
export const experimentIdParam = z.object({
  id: z.string().uuid(),
});

export const variantIdParam = z.object({
  id: z.string().uuid(),
});

// Query schemas
export const listExperimentsQuery = z.object({
  hub_id: z.string().uuid(),
});

export const listVariantsQuery = z.object({
  hub_id: z.string().uuid(),
  experiment_id: z.string().uuid().optional(),
});

export const listOverridesQuery = z.object({
  hub_id: z.string().uuid(),
  variant_id: z.string().uuid(),
});

export const deleteOverrideQuery = z.object({
  hub_id: z.string().uuid(),
  variant_id: z.string().uuid(),
  agent_id: z.string().uuid(),
});

// Body schemas — experiment
export const createExperimentBody = z.object({
  hub_id: z.string().uuid(),
  experiment_slug: slugSchema.optional(),
  experiment_name: z
    .string()
    .min(1, 'experiment_name is required')
    .max(100, 'experiment_name must be 100 characters or fewer'),
  primary_metric: z.string().max(200).nullish(),
  status: experimentStatusSchema.default('draft'),
});

export const updateExperimentBody = z.object({
  // experiment_slug is immutable after first persist — not accepted here.
  experiment_name: z
    .string()
    .min(1)
    .max(100)
    .optional(),
  primary_metric: z.string().max(200).nullish(),
  control_variant_id: z.string().uuid().nullish(),
});

export const setExperimentStatusBody = z.object({
  status: experimentStatusSchema,
});

// Body schemas — variant
export const createVariantBody = z.object({
  hub_id: z.string().uuid(),
  experiment_id: z.string().uuid(),
  label: z.string().min(1, 'label is required').max(100, 'label must be 100 characters or fewer'),
  weight: z.number().positive().default(1),
  ai_mode: variantAiModeSchema.nullish(),
  is_control: z.boolean().default(false),
});

export const updateVariantBody = z.object({
  label: z.string().min(1).max(100).optional(),
  weight: z.number().positive().optional(),
  ai_mode: variantAiModeSchema.nullish(),
  is_control: z.boolean().optional(),
});

// Body schemas — agent_variant_override (composite-key upsert)
export const upsertOverrideBody = z.object({
  hub_id: z.string().uuid(),
  variant_id: z.string().uuid(),
  agent_id: z.string().uuid(),
  instructions: z.string().nullish(),
  additional_context_template: z.string().nullish(),
  connection_id: z.string().uuid().nullish(),
  agent_settings_overlay: overlayUpdateSchema,
});

// Inferred types
export type CreateExperimentInput = z.infer<typeof createExperimentBody>;
export type UpdateExperimentInput = z.infer<typeof updateExperimentBody>;
export type SetExperimentStatusInput = z.infer<typeof setExperimentStatusBody>;
export type CreateVariantInput = z.infer<typeof createVariantBody>;
export type UpdateVariantInput = z.infer<typeof updateVariantBody>;
export type UpsertOverrideInput = z.infer<typeof upsertOverrideBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export type ExperimentStatus = 'draft' | 'running' | 'stopped';
export type VariantAiMode = 'pilot+copilot' | 'pilot' | 'copilot' | 'turned_off';

export interface ExperimentResponse {
  experiment_id: string;
  experiment_slug: string | null;
  experiment_name: string;
  status: ExperimentStatus;
  primary_metric: string | null;
  control_variant_id: string | null;
  started_at: string | null;
  stopped_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface HubVariantResponse {
  variant_id: string;
  experiment_id: string;
  label: string;
  weight: number;
  ai_mode: VariantAiMode | null;
  is_control: boolean;
  created_at: string;
  updated_at: string;
}

export interface AgentVariantOverrideResponse {
  variant_id: string;
  agent_id: string;
  instructions: string | null;
  additional_context_template: string | null;
  connection_id: string | null;
  agent_settings_overlay: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ListExperimentsResponse {
  data: ExperimentResponse[];
}

export interface GetExperimentResponse {
  data: ExperimentResponse;
}

export interface CreateExperimentResponse {
  data: ExperimentResponse;
}

export interface UpdateExperimentResponse {
  data: ExperimentResponse;
}

export interface ListHubVariantsResponse {
  data: HubVariantResponse[];
}

export interface GetHubVariantResponse {
  data: HubVariantResponse;
}

export interface CreateHubVariantResponse {
  data: HubVariantResponse;
}

export interface UpdateHubVariantResponse {
  data: HubVariantResponse;
}

export interface ListAgentVariantOverridesResponse {
  data: AgentVariantOverrideResponse[];
}

export interface UpsertAgentVariantOverrideResponse {
  data: AgentVariantOverrideResponse;
}
